import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../design';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  RefreshCw, TrendingDown, TrendingUp, ShieldCheck, AlertTriangle,
  Gauge, Bot, Pause, CheckCircle2, CircleDollarSign,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';

const usd = (n) => `$${Number(n || 0).toFixed(4)}`;
const usd2 = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AutonomyMargins() {
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [margins, setMargins] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [m, d] = await Promise.all([
        governanceAPI.getCreditSpineMargins(30),
        governanceAPI.getOrchestratorDecisions(7, 100),
      ]);
      setMargins(m.data);
      setDecisions(d.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[AutonomyMargins] fetch failed:', err);
      toast.error('Failed to load autonomy & margins data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) { setLoading(true); fetchData(); }
  }, [isAuthenticated, fetchData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="autonomy-margins-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const ready = margins?.data_ready_to_flip;
  const cheaper = margins?.direction === 'metered_cheaper_than_flat';
  const savingsPct = margins?.flat_total_usd
    ? Math.round((1 - (margins.metered_total_usd / margins.flat_total_usd)) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="autonomy-margins">
      <PageHeader
        title="Autonomy & Margins"
        subtitle={`Internal · Credit-Spine flip readiness · Updated ${lastRefresh.toLocaleTimeString()}`}
        actions={
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchData(); }}
            data-testid="autonomy-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        }
      />

      {/* Flip-readiness banner */}
      <Alert
        data-testid="flip-readiness-banner"
        className={ready
          ? (cheaper ? 'border-green-300 dark:border-green-500/50 bg-green-50 dark:bg-green-950/20'
                     : 'border-amber-300 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-950/20')
          : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40'}
      >
        {ready
          ? (cheaper ? <ShieldCheck className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />)
          : <Gauge className="h-4 w-4 text-slate-500" />}
        <AlertTitle className="font-semibold">
          {ready ? (cheaper ? 'Safe to flip to authoritative billing' : 'Review before flipping') : 'Accruing shadow data'}
        </AlertTitle>
        <AlertDescription>{margins?.recommendation}</AlertDescription>
      </Alert>

      {/* KPI strip */}
      <div className="grid gap-4 md:grid-cols-4">
        <KpiCard label="Shadow Samples" value={`${margins?.total_samples || 0} / ${margins?.sample_threshold || 200}`}
          icon={<Gauge className="h-6 w-6 text-blue-600 dark:text-blue-400" />} tint="blue"
          footer={<div className="mt-2"><Progress value={margins?.progress_pct || 0} className="h-2" /></div>}
          testid="kpi-samples" />
        <KpiCard label="Flat (charging today)" value={usd2(margins?.flat_total_usd)} tint="amber"
          icon={<CircleDollarSign className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
          sub="Current fixed-fee model" testid="kpi-flat" />
        <KpiCard label="Metered cost-plus" value={usd2(margins?.metered_total_usd)} tint="green"
          icon={<CircleDollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />}
          sub="Token-metered + 30% margin" testid="kpi-metered" />
        <KpiCard label="Delta (metered − flat)" value={usd2(margins?.delta_total_usd)} tint={cheaper ? 'green' : 'red'}
          icon={cheaper ? <TrendingDown className="h-6 w-6 text-green-600 dark:text-green-400" />
                        : <TrendingUp className="h-6 w-6 text-red-600 dark:text-red-400" />}
          sub={cheaper ? `Metered is ~${savingsPct}% cheaper` : 'Metered exceeds flat'} testid="kpi-delta" />
      </div>

      <Tabs defaultValue="margins" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-sm">
          <TabsTrigger value="margins" className="flex items-center gap-2" data-testid="tab-margins">
            <CircleDollarSign className="h-4 w-4" />Per-Model Margins
          </TabsTrigger>
          <TabsTrigger value="decisions" className="flex items-center gap-2" data-testid="tab-decisions">
            <Bot className="h-4 w-4" />Orchestrator Feed
          </TabsTrigger>
        </TabsList>

        {/* Per-model margins */}
        <TabsContent value="margins" className="mt-4">
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Per-Model: Flat vs Metered Cost-Plus</CardTitle>
            </CardHeader>
            <CardContent>
              {!margins?.per_model?.length ? (
                <EmptyRow icon={<Gauge className="h-10 w-10 text-slate-400" />}
                  title="No shadow samples yet" sub="Data accrues as the AI makes live calls." />
              ) : (
                <Table data-testid="margins-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Samples</TableHead>
                      <TableHead className="text-right">Avg Flat</TableHead>
                      <TableHead className="text-right">Avg Metered</TableHead>
                      <TableHead className="text-right">Avg Tokens (in/out)</TableHead>
                      <TableHead className="text-right">Delta</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {margins.per_model.map((m) => {
                      const cheaperRow = m.delta_total_usd <= 0;
                      return (
                        <TableRow key={m.model} data-testid={`margin-row-${m.model}`}>
                          <TableCell className="font-medium capitalize">{m.model}</TableCell>
                          <TableCell className="text-right">{m.samples}</TableCell>
                          <TableCell className="text-right">{usd(m.avg_flat_usd)}</TableCell>
                          <TableCell className="text-right">{usd(m.avg_metered_usd)}</TableCell>
                          <TableCell className="text-right text-slate-500">{m.avg_tokens_in} / {m.avg_tokens_out}</TableCell>
                          <TableCell className={`text-right font-medium ${cheaperRow ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {usd2(m.delta_total_usd)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orchestrator decisions */}
        <TabsContent value="decisions" className="mt-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <KpiCard label="Decisions (7d)" value={decisions?.total_decisions || 0}
              icon={<Bot className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />} tint="indigo" testid="kpi-decisions" />
            <KpiCard label="Acted" value={decisions?.acted_count || 0}
              icon={<CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />} tint="green" testid="kpi-acted" />
            <KpiCard label="Paused on Credits" value={decisions?.paused_on_credits || 0}
              icon={<Pause className="h-6 w-6 text-amber-600 dark:text-amber-400" />} tint="amber" testid="kpi-paused" />
          </div>
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recent Decisions (plan → action → outcome)</CardTitle>
            </CardHeader>
            <CardContent>
              {!decisions?.recent?.length ? (
                <EmptyRow icon={<Bot className="h-10 w-10 text-slate-400" />}
                  title="No orchestrator decisions yet"
                  sub="In plan_only mode, decisions appear here as inbound replies arrive." />
              ) : (
                <Table data-testid="decisions-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Lifecycle</TableHead>
                      <TableHead>Intent</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {decisions.recent.slice(0, 50).map((d, i) => (
                      <TableRow key={i} data-testid={`decision-row-${i}`}>
                        <TableCell className="text-xs text-slate-500">
                          {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{d.lifecycle_state || '—'}</Badge></TableCell>
                        <TableCell className="capitalize">{(d.intent || '—').replace(/_/g, ' ')}</TableCell>
                        <TableCell className="font-medium capitalize">{(d.action || '—').replace(/_/g, ' ')}</TableCell>
                        <TableCell><StatusBadge status={d.status} /></TableCell>
                        <TableCell className="text-right">{usd(d.cost_usd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const tintMap = {
  blue: 'bg-blue-100 dark:bg-blue-500/20', amber: 'bg-amber-100 dark:bg-amber-500/20',
  green: 'bg-green-100 dark:bg-green-500/20', red: 'bg-red-100 dark:bg-red-500/20',
  indigo: 'bg-indigo-100 dark:bg-indigo-500/20',
};

function KpiCard({ label, value, icon, tint = 'blue', sub, footer, testid }) {
  return (
    <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700" data-testid={testid}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
            <div className="text-2xl font-bold text-slate-900 dark:text-white truncate">{value}</div>
            {sub && <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${tintMap[tint]}`}>{icon}</div>
        </div>
        {footer}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    settled: 'bg-green-500 text-white', released: 'bg-slate-400 text-white',
    paused: 'bg-amber-500 text-white', done: 'bg-blue-500 text-white',
  };
  return <Badge className={map[status] || 'bg-slate-400 text-white'}>{status || '—'}</Badge>;
}

function EmptyRow({ icon, title, sub }) {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-3 w-fit">{icon}</div>
      <p className="text-lg font-medium text-slate-900 dark:text-white">{title}</p>
      <p className="text-slate-500 dark:text-slate-400">{sub}</p>
    </div>
  );
}
